import React from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useState, useCallback } from "react";
import { DataGrid, GridRowEditStopReasons } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Grid,
  Card as MuiCard,
  Divider as MuiDivider,
  Chip as MuiChip,
  Avatar,
  IconButton,
  Link,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { spacing } from "@mui/system";
import Modal from "./nuovoPreventivo";
import CircularProgress from "@mui/material/CircularProgress";
import Filters from "./Filters";
import { useGetRcTableColumns } from "./columns";
import DoughnutChart from "./DoughnutChart";
import BarChart from "./BarChart";
import { useData } from "./useData";
import CommonDrawer from "../../../components/CommonDrawer";
import useAuth from "../../../hooks/useAuth";
import {
  Home,
  Person,
  Mail,
  Phone,
  DirectionsCar,
  Settings,
  CalendarToday,
  LocalGasStation,
  FlashOn,
  LocationOn,
  Assignment,
} from "@mui/icons-material";

const Card = styled(MuiCard)(spacing);

const Divider = styled(MuiDivider)(spacing);

function RCAuto() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [filtersActive, setFiltersActive] = useState(isDesktop);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    handlePageChange,
    handlePageSizeChange,
    handleApplyFilters,
    handleRemoveFilters,
    doughnutChartData,
    barChartData,
    rcData,
    totalRows,
    loading,
    filtersLoading,
    isApplyFiltersButtonDisabled,
    pageNumber,
    rowsPerPage,
    filterOptions,
    filterData,
    setFilterData,
    setFilterByDate,
    canClearFilters,
    isRemovingFilters,
    dateFilterNumber,
    setDateFilterNumber,
    handleSaveFilters,
    lastUpdateTime,
  } = useData();

  const { getData } = useAuth();
  const navigate = useNavigate();
  const [isClienteDrawerOpen, setIsClienteDrawerOpen] = useState(false);
  const [clienteDrawerId, setClienteDrawerId] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);

  const [isTargaDrawerOpen, setIsTargaDrawerOpen] = useState(false);
  const [targaDrawerId, setTargaDrawerId] = useState(null);
  const [selectedTarga, setSelectedTarga] = useState(null);

  const [isAgenteDrawerOpen, setIsAgenteDrawerOpen] = useState(false);
  const [agenteDrawerId, setAgenteDrawerId] = useState(null);
  const [selectedAgente, setSelectedAgente] = useState(null);

  const handleClienteClick = (clientRow) => {
    setSelectedClient(clientRow);
    setClienteDrawerId(clientRow.idUID);
    setIsClienteDrawerOpen(true);
  };

  const handleTargaClick = (targaRow) => {
    setSelectedTarga(targaRow);
    setTargaDrawerId(targaRow.idUID);
    setIsTargaDrawerOpen(true);
  };

  const handleAgenteClick = (agenteRow) => {
    setSelectedAgente(agenteRow);
    setAgenteDrawerId(agenteRow.utenteIDUID);
    setIsAgenteDrawerOpen(true);
  };

  const columns = useGetRcTableColumns(
    handleApplyFilters,
    handleClienteClick,
    handleTargaClick,
    handleAgenteClick,
  );

  const switchFilterActive = () => {
    setFiltersActive((prev) => !prev);
  };

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow, isNew: false };
    return updatedRow;
  };

  return (
    <React.Fragment>
      <Modal open={isModalOpen} setOpen={setIsModalOpen} />
      <Helmet title="RC Auto" />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography variant="h3" display="inline">
            RC Auto
          </Typography>
          {lastUpdateTime && (
            <Typography fontSize={11} display="inline">
              Ultimo aggiornamento:{" "}
              {lastUpdateTime.toLocaleDateString("it-IT", {
                year: "numeric",
                month: "numeric",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })}
            </Typography>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={4}>
          <Button variant="contained" onClick={() => setIsModalOpen(true)}>
            Nuovo preventivo
          </Button>
        </Box>
      </Box>

      <Divider mt={4} />

      {loading ? (
        <Box
          sx={{
            width: "calc(100vw - 40px)",
            display: "flex",
            justifyContent: "center",
            marginTop: "16px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Grid container>
          {/* Charts */}
          <Grid
            container
            wrap="wrap"
            sx={{
              margin: isDesktop ? "0 -16px" : "0",
              padding: "0",
              width: isDesktop ? "calc(100% + 16px)" : "100%",
            }}
            spacing={isDesktop ? 4 : 0}
          >
            <Grid item lg={4} md={6} sm={12} xs={12} sx={{ flex: 1 }}>
              <DoughnutChart
                doughnutChartData={doughnutChartData}
                filterOptions={filterOptions}
                setFilterByDate={setFilterByDate}
                dateFilterNumber={dateFilterNumber}
                setDateFilterNumber={setDateFilterNumber}
              />
            </Grid>
            <Grid item lg={8} md={6} sm={12} xs={12} sx={{ flex: 1 }}>
              <BarChart
                barChartData={barChartData}
                filterOptions={filterOptions}
                setFilterByDate={setFilterByDate}
                dateFilterNumber={dateFilterNumber}
                setDateFilterNumber={setDateFilterNumber}
              />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <>
              <Card sx={{ marginBottom: "1rem" }}>
                <Filters
                  filtersActive={filtersActive}
                  switchFilterActive={switchFilterActive}
                  handleApplyFilters={handleApplyFilters}
                  handleRemoveFilters={handleRemoveFilters}
                  filtersLoading={filtersLoading}
                  isApplyFiltersButtonDisabled={isApplyFiltersButtonDisabled}
                  filterOptions={filterOptions}
                  filterData={filterData}
                  setFilterData={setFilterData}
                  canClearFilters={canClearFilters}
                  isRemovingFilters={isRemovingFilters}
                />
              </Card>

              <Card mb={6}>
                <Paper>
                  <Box sx={{ width: "100%" }}>
                    <DataGrid
                      autoHeight
                      pageSize={rowsPerPage}
                      onPageSizeChange={handlePageSizeChange}
                      rowsPerPageOptions={[5, 10, 20]}
                      pagination
                      paginationMode="server"
                      rowCount={totalRows}
                      page={pageNumber - 1}
                      onPageChange={(params) => handlePageChange(params)}
                      getRowId={(usersList) => usersList.idUID}
                      rows={rcData}
                      columns={columns}
                      editMode="row"
                      allowUpdating={true}
                      experimentalFeatures={{ newEditingApi: true }}
                      onRowEditStop={handleRowEditStop}
                      processRowUpdate={processRowUpdate}
                      disableColumnMenu
                      disableSelectionOnClick
                      localeText={{ noRowsLabel: "Non ci sono risultati" }}
                      sx={{
                        "& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-cell:focus":
                          {
                            outline: "none",
                          },
                        "& .MuiDataGrid-footerContainer": {
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "10px",
                        },
                      }}
                    />
                  </Box>
                </Paper>
              </Card>
            </>
          </Grid>
        </Grid>
      )}
      {/* Cliente Drawer */}
      <CommonDrawer
        open={isClienteDrawerOpen}
        onClose={() => setIsClienteDrawerOpen(false)}
        title={
          selectedClient
            ? selectedClient.clienteRagSociale ||
              `${selectedClient.clienteCognome} ${selectedClient.clienteNome}`
            : "Dettagli Cliente"
        }
        subtitle="Informazioni generali e contatti"
        dataId={clienteDrawerId}
        fetchDataFn={(id) =>
          getData({ action: "getPreviewInfo", type: "cliente", value: id })
            .then((res) => {
              if (
                res.status === 1 &&
                res.data &&
                Object.keys(res.data).length > 0
              ) {
                return res.data;
              }
              // Fallback mock data for example/demo
              return {
                codiceFiscale: "RSSMRA80A01H501U",
                piva: "",
                indirizzoResidenza: "Via Roma 10, 00100 Roma (RM)",
                email: "mario.rossi@example.com",
                telefono: "+39 333 1234567",
                numVeicoli: 2,
                preventivi: [
                  {
                    id: 1,
                    tipo: "RC Auto",
                    data: "12/01/2024",
                    stato: "Aperto",
                    importo: "450 €",
                    color: "warning",
                  },
                  {
                    id: 2,
                    tipo: "CVT",
                    data: "05/01/2024",
                    stato: "Chiuso",
                    importo: "1.200 €",
                    color: "success",
                  },
                ],
                tag: ["Premium", "Flotta"],
              };
            })
            .catch(() => ({
              // Fallback error mock data
              codiceFiscale: "RSSMRA80A01H501U",
              indirizzoResidenza: "Via Roma 10, 00100 Roma (RM)",
              email: "mario.rossi@example.com",
              telefono: "+39 333 1234567",
              numVeicoli: 2,
              preventivi: [
                {
                  id: 1,
                  tipo: "RC Auto",
                  data: "12/01/2024",
                  stato: "Aperto",
                  importo: "450 €",
                  color: "warning",
                },
                {
                  id: 2,
                  tipo: "CVT",
                  data: "05/01/2024",
                  stato: "Chiuso",
                  importo: "1.200 €",
                  color: "success",
                },
              ],
              tag: ["Premium", "Flotta"],
            }))
        }
        footerButtonLabel="Vai al CRM Cliente"
        onFooterButtonClick={() => {
          setIsClienteDrawerOpen(false);
          navigate(`/pages/cliente`);
        }}
        renderContent={(data) => {
          /* if (!data) return null; */
          return (
            <Box>
              {/* Header section with Tags */}
              <Box mb={6} display="flex" gap={1} flexWrap="wrap">
                {(data?.tag || ["Cliente Standard"]).map((t) => (
                  <MuiChip
                    key={t}
                    label={t}
                    size="small"
                    sx={{
                      bgcolor: "primary.light",
                      color: "primary.dark",
                      fontWeight: "bold",
                    }}
                  />
                ))}
              </Box>

              <Box mb={8}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Dati Personali
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="flex-start" gap={3}>
                      <Avatar
                        sx={{ bgcolor: "grey.100", color: "primary.main" }}
                      >
                        <Person />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Codice Fiscale / P.IVA
                        </Typography>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {data?.codiceFiscale || data?.piva || "N/D"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="flex-start" gap={3}>
                      <Avatar
                        sx={{ bgcolor: "grey.100", color: "primary.main" }}
                      >
                        <Home />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Residenza
                        </Typography>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {data?.indirizzoResidenza ||
                            "Indirizzo non disponibile"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Box mb={8}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Contatti Rapid
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box display="flex" flexDirection="column" gap={3}>
                  <Box display="flex" alignItems="center" gap={3}>
                    <IconButton
                      size="small"
                      sx={{
                        bgcolor: "primary.main",
                        color: "white",
                        "&:hover": { bgcolor: "primary.dark" },
                      }}
                    >
                      <Mail fontSize="small" />
                    </IconButton>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Email
                      </Typography>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {data?.email || "N/D"}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={3}>
                    <IconButton
                      size="small"
                      sx={{
                        bgcolor: "success.main",
                        color: "white",
                        "&:hover": { bgcolor: "success.dark" },
                      }}
                    >
                      <Phone fontSize="small" />
                    </IconButton>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Telefono
                      </Typography>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {data?.telefono || "N/D"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box mb={8}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6" fontWeight="bold">
                    Preventivi Recenti
                  </Typography>
                  <Link href="#" variant="caption" sx={{ fontWeight: "bold" }}>
                    Vedi tutti
                  </Link>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box display="flex" flexDirection="column" gap={2}>
                  {(data?.preventivi || []).map((p) => (
                    <Box
                      key={p.id}
                      sx={{
                        p: 3,
                        bgcolor: "background.default",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {p.tipo}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {p.data}
                        </Typography>
                      </Box>
                      <Box textAlign="right">
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          color="primary.main"
                        >
                          {p.importo}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: `${p.color}.main`,
                            fontWeight: "bold",
                            textTransform: "uppercase",
                            fontSize: "0.6rem",
                          }}
                        >
                          {p.stato}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Patrimonio Veicoli
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box
                  display="flex"
                  alignItems="center"
                  gap={4}
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    background:
                      "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                    color: "white",
                  }}
                >
                  <DirectionsCar fontSize="large" />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {data?.numVeicoli || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Veicoli attualmente in portafoglio
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        }}
      />
      {/* Targa Info Drawer */}
      <CommonDrawer
        open={isTargaDrawerOpen}
        onClose={() => setIsTargaDrawerOpen(false)}
        title={selectedTarga?.targa || "Dettagli Veicolo"}
        subtitle={`${selectedTarga?.marca || ""} ${
          selectedTarga?.modello || ""
        }`}
        dataId={targaDrawerId}
        fetchDataFn={(id) =>
          getData({ action: "getPreviewInfo", type: "targa", value: id }).then(
            (res) => {
              if (res.status === 1 && res.data) return res.data;
              // Mock fallback
              return {
                targa: selectedTarga?.targa || "AB123CD",
                marca: "FIAT",
                modello: "Panda",
                allestimento: "1.2 Fire Dynamic",
                dataImmatricolazione: "15/05/2020",
                alimentazione: "Benzina",
                kw: "51",
                scadenzaPolizza: "20/06/2024",
                statoPolizza: "In Vigore",
                tag: ["Attivo", "Privato"],
              };
            },
          )
        }
        footerButtonLabel="Apri Dettaglio Veicolo"
        onFooterButtonClick={() => {
          setIsTargaDrawerOpen(false);
          navigate(`/pages/rcAutoDetails/${targaDrawerId}`, {
            state: { fromDetails: true },
          });
        }}
        renderContent={(data) => {
          return (
            <Box>
              {/* Header Tags */}
              {/* <Box mb={6} display="flex" gap={1} flexWrap="wrap">
                {(data?.tag || ["Veicolo Registrato"]).map((t) => (
                  <MuiChip
                    key={t}
                    label={t}
                    size="small"
                    sx={{
                      bgcolor: "success.light",
                      color: "success.dark",
                      fontWeight: "bold",
                    }}
                  />
                ))}
              </Box> */}

              <Box mb={8}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="flex-start" gap={3}>
                      <Avatar
                        sx={{ bgcolor: "grey.100", color: "primary.main" }}
                      >
                        <DirectionsCar />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {data?.marca} {data?.modello}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {data?.allestimento}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="flex-start" gap={3}>
                      <Avatar
                        sx={{ bgcolor: "grey.100", color: "primary.main" }}
                      >
                        <CalendarToday />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Immatricolazione
                        </Typography>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {data?.dataImmatricolazione || "N/D"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Box mb={8}>
                <Grid container spacing={4}>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <LocalGasStation color="action" fontSize="small" />
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Alimentazione
                        </Typography>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {data?.alimentazione || "N/D"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <FlashOn color="action" fontSize="small" />
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Potenza / Cilindrata
                        </Typography>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {data?.kw || data?.KW || "0"} KW /{" "}
                          {data?.cilindrata || "0"} cc
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Box mb={8}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Storico Assicurativo
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    bgcolor: "background.default",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Compagnia di Provenienza
                      </Typography>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {data?.assicurazioneProvenienza || "N/D"}
                      </Typography>
                    </Box>
                    <MuiChip
                      label={`Classe ${data?.classeMerito || "N/D"}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box display="flex" justifyContent="space-between">
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Inizio Copertura
                      </Typography>
                      <Typography variant="subtitle2">
                        {data?.dataInizioCopertura || "N/D"}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="caption" color="textSecondary">
                        Fine Copertura
                      </Typography>
                      <Typography variant="subtitle2">
                        {data?.dataFineCopertura || "N/D"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                {/* //? QUI mettere i preventivi */}
              </Box>

              {/* <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Azioni Veloci
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box
                  display="flex"
                  alignItems="center"
                  gap={4}
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    background:
                      "linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)",
                    color: "white",
                  }}
                >
                  <Settings fontSize="large" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Gestione Veicolo
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Visualizza lo storico completo e gestisci i dati.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      bgcolor: "white",
                      color: "orange",
                      "&:hover": { bgcolor: "#f5f5f5" },
                    }}
                    onClick={() => {
                      setIsTargaDrawerOpen(false);
                      navigate(`/pages/rcAutoDetails/${targaDrawerId}`, { state: { fromDetails: true } });
                    }}
                  >
                    Vedi
                  </Button>
                </Box>
              </Box> */}
            </Box>
          );
        }}
      />
      {/* Agente Drawer */}
      <CommonDrawer
        open={isAgenteDrawerOpen}
        onClose={() => setIsAgenteDrawerOpen(false)}
        title={selectedAgente?.targa || "Dettagli Agente"}
        subtitle={`${selectedAgente?.marca || ""} ${
          selectedAgente?.modello || ""
        }`}
        dataId={targaDrawerId}
        fetchDataFn={(id) =>
          getData({ action: "getPreviewInfo", type: "utente", value: id }).then(
            (res) => {
              if (res.status === 1 && res.data) return res.data;
            },
          )
        }
        footerButtonLabel="Apri Dettaglio Agente"
        onFooterButtonClick={() => {
          setIsAgenteDrawerOpen(false);
          /* navigate(`/pages/rcAutoDetails/${targaDrawerId}`); */
        }}
        renderContent={(data) => {
          return (
            <Box>
              <Box mb={8}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="flex-start" gap={3}>
                      <Avatar
                        sx={{ bgcolor: "grey.100", color: "primary.main" }}
                      >
                        <DirectionsCar />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {data?.marca} {data?.modello}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {data?.allestimento}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="flex-start" gap={3}>
                      <Avatar
                        sx={{ bgcolor: "grey.100", color: "primary.main" }}
                      >
                        <CalendarToday />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Immatricolazione
                        </Typography>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {data?.dataImmatricolazione || "N/D"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Box mb={8}>
                <Grid container spacing={4}>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <LocalGasStation color="action" fontSize="small" />
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Alimentazione
                        </Typography>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {data?.alimentazione || "N/D"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <FlashOn color="action" fontSize="small" />
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Potenza / Cilindrata
                        </Typography>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {data?.kw || data?.KW || "0"} KW /{" "}
                          {data?.cilindrata || "0"} cc
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Box mb={8}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Storico Assicurativo
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    bgcolor: "background.default",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Compagnia di Provenienza
                      </Typography>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {data?.assicurazioneProvenienza || "N/D"}
                      </Typography>
                    </Box>
                    <MuiChip
                      label={`Classe ${data?.classeMerito || "N/D"}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box display="flex" justifyContent="space-between">
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Inizio Copertura
                      </Typography>
                      <Typography variant="subtitle2">
                        {data?.dataInizioCopertura || "N/D"}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="caption" color="textSecondary">
                        Fine Copertura
                      </Typography>
                      <Typography variant="subtitle2">
                        {data?.dataFineCopertura || "N/D"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                {/* //? QUI mettere i preventivi */}
              </Box>
            </Box>
          );
        }}
      />
      <CommonDrawer
        open={isAgenteDrawerOpen}
        onClose={() => setIsAgenteDrawerOpen(false)}
        title={
          selectedAgente
            ? `${selectedAgente.utenteCognome} ${selectedAgente.utenteNome}`
            : "Dettagli Agente"
        }
        subtitle={selectedAgente?.utenteAzienda || "Agenzia Partner"}
        dataId={agenteDrawerId}
        fetchDataFn={(id) =>
          getData({ action: "getPreviewInfo", type: "utente", value: id }).then(
            (res) => {
              if (res.status === 1 && res.data) return res.data;
              // Mock fallback based on new structure
              return {
                generale: {
                  Nominativo:
                    `${selectedAgente?.utenteCognome} ${selectedAgente?.utenteNome}`.toUpperCase() ||
                    "DE FELICE RAFFAELE",
                  Indirizzo: "VIA ANTONIO GRAMSCI 114",
                  Comune: "Boscoreale (NA)",
                },
                RUI: {
                  sezione: "E",
                  codice: "000500985",
                  data: "14/10/2014",
                },
                contatti: [
                  { Tipologia: "Cellulare", Valore: "3355281278" },
                  { Tipologia: "Email", Valore: "Agenzia@iotiassicuro.com" },
                ],
                tag: ["Agente Verificato", "Partner"],
              };
            },
          )
        }
        footerButtonLabel="Apri Profilo Agente"
        onFooterButtonClick={() => {
          setIsAgenteDrawerOpen(false);
          navigate(`/pages/profile/${agenteDrawerId}`);
        }}
        renderContent={(data) => {
          const email = data?.contatti?.find(
            (c) => c.Tipologia === "Email",
          )?.Valore;
          const phone = data?.contatti?.find(
            (c) => c.Tipologia === "Cellulare",
          )?.Valore;

          return (
            <Box>
              <Box mb={8}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Recapiti e Sede
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box display="flex" flexDirection="column" gap={3}>
                  {email && (
                    <Box display="flex" alignItems="center" gap={3}>
                      <IconButton
                        size="small"
                        sx={{ bgcolor: "grey.100", color: "primary.main" }}
                      >
                        <Mail fontSize="small" />
                      </IconButton>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Email
                        </Typography>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {email}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {phone && (
                    <Box display="flex" alignItems="center" gap={3}>
                      <IconButton
                        size="small"
                        sx={{ bgcolor: "grey.100", color: "success.main" }}
                      >
                        <Phone fontSize="small" />
                      </IconButton>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Cellulare
                        </Typography>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {phone}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  <Box display="flex" alignItems="center" gap={3}>
                    <IconButton
                      size="small"
                      sx={{ bgcolor: "grey.100", color: "info.main" }}
                    >
                      <LocationOn fontSize="small" />
                    </IconButton>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Indirizzo Sede
                      </Typography>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {data?.generale?.Indirizzo || "N/D"},{" "}
                        {data?.generale?.Comune || "N/D"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box mb={8}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Iscrizione R.U.I.
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    bgcolor: "background.default",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="textSecondary">
                        Sezione
                      </Typography>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {data?.RUI?.sezione || "N/D"}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="textSecondary">
                        Codice
                      </Typography>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {data?.RUI?.codice || "N/D"}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="textSecondary">
                        Dal
                      </Typography>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {data?.RUI?.data || "N/D"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>
          );
        }}
      />
    </React.Fragment>
  );
}

export default RCAuto;
